// src/capabilities/CaseStudyModal.js
// Alta / edición / vista de un caso de estudio del catálogo (colección caseStudies).
// Ver PLAN_CASOS_ESTUDIO.md §4.1
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';

const MIN_READING_TIME = 5;
const MAX_READING_TIME = 60;
const DEFAULT_READING_TIME = 5;

const CaseStudyModal = ({
  open,
  onClose,
  mode, // 'create' | 'edit' | 'view'
  selectedCaseStudy,
  onSave,
  user,
  authToken,
  showAlert,
  language,
}) => {
  const [formData, setFormData] = useState({ title: '', content: '', readingTime: DEFAULT_READING_TIME });
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const { organization } = useStore.getState();

  useEffect(() => {
    if (!open) return;
    setValidationErrors([]);
    setShowValidationErrors(false);
    if (mode === 'create' || !selectedCaseStudy) {
      setFormData({ title: '', content: '', readingTime: DEFAULT_READING_TIME });
    } else {
      setFormData({
        title: selectedCaseStudy.title || '',
        content: selectedCaseStudy.content || '',
        readingTime: Number(selectedCaseStudy.readingTime) || DEFAULT_READING_TIME,
      });
    }
  }, [open, mode, selectedCaseStudy]);

  const validateForm = () => {
    const errors = [];
    if (!formData.title.trim()) errors.push(t('caseStudies.titleError'));
    if (!formData.content.trim()) errors.push(t('caseStudies.contentError'));
    const rt = Number(formData.readingTime);
    if (!Number.isFinite(rt) || rt < MIN_READING_TIME || rt > MAX_READING_TIME) {
      errors.push(t('caseStudies.readingTimeError', { min: MIN_READING_TIME, max: MAX_READING_TIME }));
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (showValidationErrors) setShowValidationErrors(false);
  };

  const handleClose = () => {
    setValidationErrors([]);
    setShowValidationErrors(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowValidationErrors(true);
      return;
    }
    try {
      setLoading(true);
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        readingTime: Number(formData.readingTime),
        lastUpdatedBy: user?.email || user?.uid || 'unknown',
        language,
        organizationId: organization,
      };
      const headers = { Authorization: `Bearer ${authToken}` };
      let response;
      if (mode === 'create') {
        response = await api.post('/case-studies', payload, { headers });
      } else {
        const id = selectedCaseStudy.id || selectedCaseStudy.uid;
        response = await api.put(`/case-studies/${id}`, payload, { headers });
      }

      if (response.data.response && response.data.response.status_response === 'OK') {
        handleClose();
        onSave();
        showAlert(t(mode === 'create' ? 'caseStudies.created' : 'caseStudies.updated'), 'success');
      } else {
        showAlert(response.data.response?.message || t('caseStudies.saveError'), 'error');
      }
    } catch (err) {
      console.error('Error saving case study:', err);
      showAlert(t('caseStudies.saveError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.title.trim() !== '' && formData.content.trim() !== '';
  const readOnly = mode === 'view';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' && t('caseStudies.newCaseStudy')}
        {mode === 'edit' && t('caseStudies.editCaseStudy')}
        {mode === 'view' && t('caseStudies.showCaseStudy')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showValidationErrors && validationErrors.length > 0 && !readOnly && (
            <Alert severity="error">
              <Typography variant="subtitle2">{t('validationErrors')}:</Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <TextField
            label={`${t('title')} *`}
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            disabled={readOnly}
          />

          <TextField
            label={`${t('content')} *`}
            value={formData.content}
            onChange={(e) => handleChange('content', e.target.value)}
            fullWidth
            multiline
            minRows={8}
            disabled={readOnly}
            helperText={t('caseStudies.contentHelper')}
          />

          <TextField
            label={t('caseStudies.readingTime')}
            type="number"
            value={formData.readingTime}
            onChange={(e) => handleChange('readingTime', e.target.value)}
            disabled={readOnly}
            inputProps={{ min: MIN_READING_TIME, max: MAX_READING_TIME, step: 1 }}
            helperText={t('caseStudies.readingTimeHelper', { min: MIN_READING_TIME, max: MAX_READING_TIME })}
            sx={{ width: 260 }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{readOnly ? t('close') : t('cancel')}</Button>
        {!readOnly && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !canSubmit}>
            {loading ? t('saving') : t('save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CaseStudyModal;
